import { useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  CrfField,
  CrfFieldChoice,
  FieldDataType,
  FieldValidation,
  FieldFormula,
  FieldDisplayCondition,
  ClinicalPreset,
  ConditionType,
  ConditionOperator,
  AlternateUnit,
  CodingDictionary,
  CrossFieldRule,
} from "@/api/protocol-types";

// ── Constants ───────────────────────────────────────────

const DATA_TYPES: { value: FieldDataType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "integer", label: "Integer" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "datetime", label: "Date & Time" },
  { value: "boolean", label: "Boolean" },
  { value: "select", label: "Select (Dropdown)" },
  { value: "multi_select", label: "Multi-Select" },
  { value: "radio", label: "Radio" },
  { value: "textarea", label: "Textarea" },
  { value: "calculated", label: "Calculated" },
  { value: "file", label: "File Upload" },
  { value: "signature", label: "Signature" },
];

const CLINICAL_PRESETS: { value: ClinicalPreset; label: string }[] = [
  { value: "bmi", label: "BMI (Body Mass Index)" },
  { value: "egfr", label: "eGFR (Estimated GFR)" },
  { value: "bsa", label: "BSA (Body Surface Area)" },
  { value: "creatinine_clearance", label: "Creatinine Clearance" },
  { value: "age_from_dob", label: "Age from DOB" },
  { value: "study_day", label: "Study Day" },
];

const CONDITION_TYPES: { value: ConditionType; label: string }[] = [
  { value: "show_when", label: "Show when" },
  { value: "hide_when", label: "Hide when" },
  { value: "enable_when", label: "Enable when" },
  { value: "require_when", label: "Require when" },
];

const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
  { value: "greater_than", label: "greater than" },
  { value: "less_than", label: "less than" },
  { value: "contains", label: "contains" },
  { value: "not_empty", label: "is not empty" },
  { value: "is_empty", label: "is empty" },
  { value: "in", label: "in list" },
];

const WIDTH_OPTIONS: { value: CrfField["width"]; label: string }[] = [
  { value: "full", label: "Full width" },
  { value: "half", label: "Half width" },
  { value: "third", label: "Third width" },
];

const CHOICE_DATA_TYPES: FieldDataType[] = ["select", "multi_select", "radio"];

// ── Helpers ─────────────────────────────────────────────

const inputCls =
  "w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30";

const selectCls =
  "w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30";

const labelCls = "block text-[11px] font-medium text-muted-foreground mb-1";

const sectionBtnCls =
  "flex w-full items-center gap-1.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors";

// ── Props ───────────────────────────────────────────────

interface CrfFieldEditorProps {
  field: CrfField;
  /** All fields in the same CRF (for display condition source picker) */
  siblingFields: CrfField[];
  onChange: (patch: Partial<CrfField>) => void;
  onDelete: () => void;
}

// ── Component ───────────────────────────────────────────

export function CrfFieldEditor({
  field,
  siblingFields,
  onChange,
  onDelete,
}: CrfFieldEditorProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(["basic"]),
  );

  const toggle = (key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hasChoices = CHOICE_DATA_TYPES.includes(field.dataType);
  const isCalculated = field.dataType === "calculated";

  return (
    <div className="space-y-1 border-t border-border bg-muted/30 px-4 py-3">
      {/* ── Basic Properties ──────────────────────── */}
      <SectionHeader
        label="Properties"
        sectionKey="basic"
        open={openSections.has("basic")}
        onToggle={toggle}
      />
      {openSections.has("basic") && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pb-2">
          <div>
            <label className={labelCls}>Label</label>
            <input
              className={inputCls}
              value={field.label}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Field Name</label>
            <input
              className={inputCls}
              value={field.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="e.g. subject_age"
            />
          </div>
          <div>
            <label className={labelCls}>Data Type</label>
            <select
              className={selectCls}
              value={field.dataType}
              onChange={(e) =>
                onChange({ dataType: e.target.value as FieldDataType })
              }
            >
              {DATA_TYPES.map((dt) => (
                <option key={dt.value} value={dt.value}>
                  {dt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Width</label>
            <select
              className={selectCls}
              value={field.width}
              onChange={(e) =>
                onChange({ width: e.target.value as CrfField["width"] })
              }
            >
              {WIDTH_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Unit</label>
            <input
              className={inputCls}
              value={field.unit ?? ""}
              onChange={(e) =>
                onChange({ unit: e.target.value || undefined })
              }
              placeholder="e.g. kg, mmHg"
            />
          </div>
          <div>
            <label className={labelCls}>Hint Label</label>
            <input
              className={inputCls}
              value={field.hintLabel ?? ""}
              onChange={(e) =>
                onChange({ hintLabel: e.target.value || undefined })
              }
              placeholder="Helper text for data entry"
            />
          </div>
          <div className="col-span-2 flex items-center gap-6">
            <label className="flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onChange({ required: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary/30"
              />
              Required
            </label>
            <label className="flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                checked={field.readOnly ?? false}
                onChange={(e) => onChange({ readOnly: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary/30"
              />
              Read-only
            </label>
          </div>
        </div>
      )}

      {/* ── Validation ────────────────────────────── */}
      <SectionHeader
        label="Validation"
        sectionKey="validation"
        open={openSections.has("validation")}
        onToggle={toggle}
        badge={field.validation ? "configured" : undefined}
      />
      {openSections.has("validation") && (
        <ValidationEditor
          validation={field.validation}
          dataType={field.dataType}
          onChange={(v) => onChange({ validation: v })}
        />
      )}

      {/* ── Choices (select/radio/multi_select) ─── */}
      {hasChoices && (
        <>
          <SectionHeader
            label="Choices"
            sectionKey="choices"
            open={openSections.has("choices")}
            onToggle={toggle}
            badge={
              field.choices?.length
                ? `${field.choices.length}`
                : undefined
            }
          />
          {openSections.has("choices") && (
            <ChoicesEditor
              choices={field.choices ?? []}
              onChange={(c) => onChange({ choices: c })}
            />
          )}
        </>
      )}

      {/* ── Formula (calculated fields) ───────── */}
      {isCalculated && (
        <>
          <SectionHeader
            label="Formula"
            sectionKey="formula"
            open={openSections.has("formula")}
            onToggle={toggle}
            badge={field.formula?.clinicalPreset ?? (field.formula ? "custom" : undefined)}
          />
          {openSections.has("formula") && (
            <FormulaEditor
              formula={field.formula}
              siblingFields={siblingFields}
              onChange={(f) => onChange({ formula: f })}
            />
          )}
        </>
      )}

      {/* ── Display Condition ─────────────────── */}
      <SectionHeader
        label="Display Condition"
        sectionKey="condition"
        open={openSections.has("condition")}
        onToggle={toggle}
        badge={field.displayCondition ? field.displayCondition.type : undefined}
      />
      {openSections.has("condition") && (
        <DisplayConditionEditor
          condition={field.displayCondition}
          siblingFields={siblingFields.filter((f) => f.id !== field.id)}
          onChange={(c) => onChange({ displayCondition: c })}
        />
      )}

      {/* ── CDASH Mapping ─────────────────────── */}
      <SectionHeader
        label="CDASH Mapping"
        sectionKey="cdash"
        open={openSections.has("cdash")}
        onToggle={toggle}
        badge={field.cdashVariable ? field.cdashVariable : undefined}
      />
      {openSections.has("cdash") && (
        <div className="grid grid-cols-3 gap-x-4 gap-y-2.5 pb-2">
          <div>
            <label className={labelCls}>Domain</label>
            <input
              className={inputCls}
              value={field.cdashDomain ?? ""}
              onChange={(e) =>
                onChange({ cdashDomain: e.target.value || undefined })
              }
              placeholder="e.g. DM, VS, AE"
            />
          </div>
          <div>
            <label className={labelCls}>Variable</label>
            <input
              className={inputCls}
              value={field.cdashVariable ?? ""}
              onChange={(e) =>
                onChange({ cdashVariable: e.target.value || undefined })
              }
              placeholder="e.g. BRTHDTC"
            />
          </div>
          <div>
            <label className={labelCls}>External ID</label>
            <input
              className={inputCls}
              value={field.externalId ?? ""}
              onChange={(e) =>
                onChange({ externalId: e.target.value || undefined })
              }
              placeholder="EDC field ID"
            />
          </div>
        </div>
      )}

      {/* ── Advanced (Units & Coding) ─────────── */}
      <SectionHeader
        label="Advanced"
        sectionKey="advanced"
        open={openSections.has("advanced")}
        onToggle={toggle}
      />
      {openSections.has("advanced") && (
        <div className="space-y-3 pb-2">
          <AlternateUnitsEditor
            units={field.alternateUnits ?? []}
            onChange={(u) => onChange({ alternateUnits: u.length ? u : undefined })}
          />
          <CodingDictionaryEditor
            dictionary={field.codingDictionary}
            onChange={(d) => onChange({ codingDictionary: d })}
          />
        </div>
      )}

      {/* ── Delete ────────────────────────────── */}
      <div className="flex justify-end border-t border-border pt-2">
        <button
          onClick={onDelete}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          Delete field
        </button>
      </div>
    </div>
  );
}

// ── Section Header ──────────────────────────────────────

function SectionHeader({
  label,
  sectionKey,
  open,
  onToggle,
  badge,
}: {
  label: string;
  sectionKey: string;
  open: boolean;
  onToggle: (key: string) => void;
  badge?: string;
}) {
  return (
    <button
      onClick={() => onToggle(sectionKey)}
      className={sectionBtnCls}
    >
      {open ? (
        <ChevronDown className="h-3 w-3" />
      ) : (
        <ChevronRight className="h-3 w-3" />
      )}
      {label}
      {badge && (
        <span className="ml-auto rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-primary">
          {badge}
        </span>
      )}
    </button>
  );
}

// ── Validation Editor ───────────────────────────────────

function ValidationEditor({
  validation,
  dataType,
  onChange,
}: {
  validation?: FieldValidation;
  dataType: FieldDataType;
  onChange: (v: FieldValidation | undefined) => void;
}) {
  const v = validation ?? {};
  const update = (patch: Partial<FieldValidation>) => {
    const next = { ...v, ...patch };
    // Remove undefined/empty keys
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(
        ([, val]) => val !== undefined && val !== "" && val !== null,
      ),
    ) as FieldValidation;
    onChange(Object.keys(cleaned).length > 0 ? cleaned : undefined);
  };

  const isNumeric = ["number", "integer"].includes(dataType);
  const isText = ["text", "textarea"].includes(dataType);
  const isDate = ["date", "datetime"].includes(dataType);

  return (
    <div className="space-y-3 pb-2">
      {isNumeric && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <div>
            <label className={labelCls}>Min Value</label>
            <input
              type="number"
              className={inputCls}
              value={v.min ?? ""}
              onChange={(e) =>
                update({ min: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Max Value</label>
            <input
              type="number"
              className={inputCls}
              value={v.max ?? ""}
              onChange={(e) =>
                update({ max: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      )}
      {isText && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <div>
            <label className={labelCls}>Min Length</label>
            <input
              type="number"
              className={inputCls}
              value={v.minLength ?? ""}
              onChange={(e) =>
                update({
                  minLength: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Max Length</label>
            <input
              type="number"
              className={inputCls}
              value={v.maxLength ?? ""}
              onChange={(e) =>
                update({
                  maxLength: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
        </div>
      )}
      {isDate && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <div>
            <label className={labelCls}>Min Date</label>
            <input
              type="date"
              className={inputCls}
              value={v.minDate ?? ""}
              onChange={(e) =>
                update({ minDate: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Max Date</label>
            <input
              type="date"
              className={inputCls}
              value={v.maxDate ?? ""}
              onChange={(e) =>
                update({ maxDate: e.target.value || undefined })
              }
            />
          </div>
        </div>
      )}
      <div>
        <label className={labelCls}>Regex Pattern</label>
        <input
          className={inputCls}
          value={v.regex ?? ""}
          onChange={(e) => update({ regex: e.target.value || undefined })}
          placeholder="e.g. ^[A-Z]{2}-\\d{4}$"
        />
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={v.queryOnFail ?? false}
            onChange={(e) => update({ queryOnFail: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary/30"
          />
          Open query on validation failure
        </label>
      </div>
      {v.queryOnFail && (
        <div>
          <label className={labelCls}>Query Text</label>
          <input
            className={inputCls}
            value={v.queryText ?? ""}
            onChange={(e) => update({ queryText: e.target.value || undefined })}
            placeholder="Discrepancy description for auto-generated query"
          />
        </div>
      )}

      {/* Cross-field rules */}
      <CrossFieldRulesEditor
        rules={v.crossFieldRules ?? []}
        onChange={(rules) =>
          update({ crossFieldRules: rules.length ? rules : undefined })
        }
      />
    </div>
  );
}

// ── Cross-Field Rules ───────────────────────────────────

function CrossFieldRulesEditor({
  rules,
  onChange,
}: {
  rules: CrossFieldRule[];
  onChange: (rules: CrossFieldRule[]) => void;
}) {
  const addRule = () => {
    onChange([
      ...rules,
      {
        id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        expression: "",
        message: "",
        severity: "error" as const,
      },
    ]);
  };

  const updateRule = (idx: number, patch: Partial<CrossFieldRule>) => {
    const next = [...rules];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeRule = (idx: number) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className={labelCls}>Cross-Field Rules</span>
        <button
          onClick={addRule}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
        >
          <Plus className="h-3 w-3" />
          Add rule
        </button>
      </div>
      {rules.map((rule, idx) => (
        <div
          key={rule.id}
          className="mt-1.5 flex items-start gap-2 rounded-md border border-border bg-background p-2"
        >
          <div className="flex-1 space-y-1.5">
            <input
              className={inputCls}
              value={rule.expression}
              onChange={(e) => updateRule(idx, { expression: e.target.value })}
              placeholder="e.g. weight > 0 && height > 0"
            />
            <div className="flex gap-1.5">
              <input
                className={cn(inputCls, "flex-1")}
                value={rule.message ?? ""}
                onChange={(e) => updateRule(idx, { message: e.target.value })}
                placeholder="Error message"
              />
              <select
                className={cn(selectCls, "w-24")}
                value={rule.severity}
                onChange={(e) =>
                  updateRule(idx, {
                    severity: e.target.value as "error" | "warning",
                  })
                }
              >
                <option value="error">Error</option>
                <option value="warning">Warning</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => removeRule(idx)}
            className="mt-1 text-muted-foreground hover:text-red-600"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Choices Editor ──────────────────────────────────────

function ChoicesEditor({
  choices,
  onChange,
}: {
  choices: CrfFieldChoice[];
  onChange: (choices: CrfFieldChoice[]) => void;
}) {
  const addChoice = () => {
    onChange([
      ...choices,
      { value: "", label: "", order: choices.length },
    ]);
  };

  const updateChoice = (idx: number, patch: Partial<CrfFieldChoice>) => {
    const next = [...choices];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeChoice = (idx: number) => {
    const next = choices.filter((_, i) => i !== idx);
    next.forEach((c, i) => (c.order = i));
    onChange(next);
  };

  const moveChoice = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= choices.length) return;
    const next = [...choices];
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((c, i) => (c.order = i));
    onChange(next);
  };

  return (
    <div className="pb-2">
      <div className="space-y-1.5">
        {choices.map((choice, idx) => (
          <div
            key={idx}
            className="flex items-center gap-1.5"
          >
            <div className="flex flex-col">
              <button
                onClick={() => moveChoice(idx, -1)}
                disabled={idx === 0}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronDown className="h-2.5 w-2.5 rotate-180" />
              </button>
              <button
                onClick={() => moveChoice(idx, 1)}
                disabled={idx >= choices.length - 1}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <ChevronDown className="h-2.5 w-2.5" />
              </button>
            </div>
            <input
              className={cn(inputCls, "flex-1")}
              value={choice.value}
              onChange={(e) => updateChoice(idx, { value: e.target.value })}
              placeholder="Value"
            />
            <input
              className={cn(inputCls, "flex-1")}
              value={choice.label}
              onChange={(e) => updateChoice(idx, { label: e.target.value })}
              placeholder="Display label"
            />
            <button
              onClick={() => removeChoice(idx)}
              className="text-muted-foreground hover:text-red-600"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addChoice}
        className="mt-2 flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
      >
        <Plus className="h-3 w-3" />
        Add choice
      </button>
    </div>
  );
}

// ── Formula Editor ──────────────────────────────────────

function FormulaEditor({
  formula,
  siblingFields,
  onChange,
}: {
  formula?: FieldFormula;
  siblingFields: CrfField[];
  onChange: (f: FieldFormula | undefined) => void;
}) {
  const f = formula ?? {
    expression: "",
    recalculateOn: [],
  };

  const update = (patch: Partial<FieldFormula>) => {
    const next = { ...f, ...patch };
    if (!next.expression && !next.clinicalPreset) {
      onChange(undefined);
      return;
    }
    onChange(next);
  };

  const toggleRecalcField = (fieldId: string) => {
    const current = f.recalculateOn ?? [];
    const next = current.includes(fieldId)
      ? current.filter((id) => id !== fieldId)
      : [...current, fieldId];
    update({ recalculateOn: next });
  };

  return (
    <div className="space-y-2.5 pb-2">
      <div>
        <label className={labelCls}>Clinical Preset</label>
        <select
          className={selectCls}
          value={f.clinicalPreset ?? ""}
          onChange={(e) =>
            update({
              clinicalPreset: (e.target.value as ClinicalPreset) || undefined,
            })
          }
        >
          <option value="">Custom expression</option>
          {CLINICAL_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Expression</label>
        <input
          className={inputCls}
          value={f.expression}
          onChange={(e) => update({ expression: e.target.value })}
          placeholder="e.g. weight / (height / 100) ^ 2"
        />
      </div>
      <div className="grid grid-cols-2 gap-x-4">
        <div>
          <label className={labelCls}>Precision (decimal places)</label>
          <input
            type="number"
            className={inputCls}
            value={f.precision ?? ""}
            onChange={(e) =>
              update({
                precision: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            min={0}
            max={10}
          />
        </div>
        <div>
          <label className={labelCls}>Result Unit</label>
          <input
            className={inputCls}
            value={f.unit ?? ""}
            onChange={(e) => update({ unit: e.target.value || undefined })}
            placeholder="e.g. kg/m²"
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Recalculate when these fields change</label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {siblingFields
            .filter((sf) => sf.dataType !== "calculated")
            .map((sf) => {
              const selected = (f.recalculateOn ?? []).includes(sf.id);
              return (
                <button
                  key={sf.id}
                  onClick={() => toggleRecalcField(sf.id)}
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors",
                    selected
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50",
                  )}
                >
                  {sf.label || sf.name}
                </button>
              );
            })}
          {siblingFields.filter((sf) => sf.dataType !== "calculated")
            .length === 0 && (
            <span className="text-[10px] text-muted-foreground">
              No source fields available
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Display Condition Editor ────────────────────────────

function DisplayConditionEditor({
  condition,
  siblingFields,
  onChange,
}: {
  condition?: FieldDisplayCondition;
  siblingFields: CrfField[];
  onChange: (c: FieldDisplayCondition | undefined) => void;
}) {
  if (!condition && siblingFields.length === 0) {
    return (
      <p className="pb-2 text-[10px] text-muted-foreground">
        No sibling fields available to base conditions on.
      </p>
    );
  }

  const addCondition = () => {
    onChange({
      type: "show_when",
      sourceFieldId: siblingFields[0]?.id ?? "",
      operator: "equals",
      value: "",
    });
  };

  if (!condition) {
    return (
      <div className="pb-2">
        <button
          onClick={addCondition}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
        >
          <Plus className="h-3 w-3" />
          Add condition
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2.5 pb-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        <div>
          <label className={labelCls}>Condition Type</label>
          <select
            className={selectCls}
            value={condition.type}
            onChange={(e) =>
              onChange({ ...condition, type: e.target.value as ConditionType })
            }
          >
            {CONDITION_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>
                {ct.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Source Field</label>
          <select
            className={selectCls}
            value={condition.sourceFieldId}
            onChange={(e) =>
              onChange({ ...condition, sourceFieldId: e.target.value })
            }
          >
            <option value="">Select field...</option>
            {siblingFields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label || f.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Operator</label>
          <select
            className={selectCls}
            value={condition.operator}
            onChange={(e) =>
              onChange({
                ...condition,
                operator: e.target.value as ConditionOperator,
              })
            }
          >
            {CONDITION_OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Value</label>
          <input
            className={inputCls}
            value={String(condition.value ?? "")}
            onChange={(e) =>
              onChange({ ...condition, value: e.target.value })
            }
            placeholder="Comparison value"
          />
        </div>
      </div>
      <button
        onClick={() => onChange(undefined)}
        className="flex items-center gap-1 text-[11px] font-medium text-red-600 hover:text-red-500"
      >
        <Trash2 className="h-3 w-3" />
        Remove condition
      </button>
    </div>
  );
}

// ── Alternate Units Editor ──────────────────────────────

function AlternateUnitsEditor({
  units,
  onChange,
}: {
  units: AlternateUnit[];
  onChange: (units: AlternateUnit[]) => void;
}) {
  const addUnit = () => {
    onChange([...units, { unit: "", conversionFactor: 1 }]);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className={labelCls}>Alternate Units</span>
        <button
          onClick={addUnit}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
        >
          <Plus className="h-3 w-3" />
          Add unit
        </button>
      </div>
      {units.map((u, idx) => (
        <div key={idx} className="mt-1.5 flex items-center gap-2">
          <input
            className={cn(inputCls, "flex-1")}
            value={u.unit}
            onChange={(e) => {
              const next = [...units];
              next[idx] = { ...next[idx], unit: e.target.value };
              onChange(next);
            }}
            placeholder="Unit name"
          />
          <input
            type="number"
            className={cn(inputCls, "w-28")}
            value={u.conversionFactor}
            onChange={(e) => {
              const next = [...units];
              next[idx] = {
                ...next[idx],
                conversionFactor: Number(e.target.value) || 1,
              };
              onChange(next);
            }}
            step="any"
            placeholder="Factor"
          />
          <button
            onClick={() => onChange(units.filter((_, i) => i !== idx))}
            className="text-muted-foreground hover:text-red-600"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Coding Dictionary Editor ────────────────────────────

function CodingDictionaryEditor({
  dictionary,
  onChange,
}: {
  dictionary?: CodingDictionary;
  onChange: (d: CodingDictionary | undefined) => void;
}) {
  if (!dictionary) {
    return (
      <div>
        <span className={labelCls}>Coding Dictionary</span>
        <button
          onClick={() => onChange({ name: "", version: "" })}
          className="mt-1 flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
        >
          <Plus className="h-3 w-3" />
          Add coding dictionary
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className={labelCls}>Coding Dictionary</span>
        <button
          onClick={() => onChange(undefined)}
          className="text-[10px] font-medium text-red-600 hover:text-red-500"
        >
          Remove
        </button>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-x-4">
        <input
          className={inputCls}
          value={dictionary.name}
          onChange={(e) => onChange({ ...dictionary, name: e.target.value })}
          placeholder="e.g. MedDRA, WHO Drug"
        />
        <input
          className={inputCls}
          value={dictionary.version}
          onChange={(e) => onChange({ ...dictionary, version: e.target.value })}
          placeholder="e.g. 27.0"
        />
      </div>
    </div>
  );
}
